/**
 * `RealtimeConversationLog.ts` — web port of Unity's
 * `AI/RealtimeConversationFileLogger.cs`.
 *
 * Unity writes the realtime request/response flow, tool calls, and cloud
 * message payloads to `StreamingAssets/AI/RealtimeLogs/realtime-session-*.log`
 * and keeps the latest five files. A browser has no such directory and no
 * local file path to put one that a developer could then read back
 * mid-session, so the web keeps the same CONTENT in a rolling in-memory buffer
 * that the debug panel's Log tab renders live, mirrors each entry to the
 * browser console, and optionally hands entries to a durable recorder. Same
 * purpose, same redaction posture (none — this is the developer-facing wire
 * log, not the redacted telemetry in `AIObservabilityModel`), different sinks.
 *
 * **Audio never reaches here, by construction rather than by filtering.** Mic
 * frames (`input_audio_buffer.append`) are sent straight from
 * `AIRealtimeTransportModel`, and agent audio deltas are peeled off in the same
 * transport before `onMessage` fires — so `RealtimeSession`, which is what
 * feeds this log, only ever sees the non-audio protocol. Should either ever be
 * routed through the session, drop it here explicitly: a single reply is
 * megabytes of base64 and would evict the whole buffer.
 */

import { Emitter } from "@core/events/emitter";

export type RealtimeLogDirection = "out" | "in" | "tool" | "note" | "error";

export interface RealtimeLogEntry {
  /** Monotonic, so a renderer can key rows without hashing the payload. */
  readonly id: number;
  /** Monotonic local timestamp used by the console mirror and durable log rows. */
  readonly at: number;
  readonly direction: RealtimeLogDirection;
  /** Protocol event type (`session.update`, `response.done`) or a tool name. */
  readonly type: string;
  /** One-line detail rendered beside the type. */
  readonly summary?: string;
  /** Pretty-printed JSON body, truncated — absent when there is nothing to show. */
  readonly payload?: string;
}

export interface RealtimeLogRecordedEvent {
  readonly timestamp: number;
  readonly direction: RealtimeLogDirection;
  readonly type: string;
  readonly summary?: string;
  readonly payload?: string;
}

export type RealtimeLogRecorder = (event: RealtimeLogRecordedEvent) => void;

/**
 * Entries kept. Sized for a working session rather than a whole one: the panel
 * is a live window, and an unbounded log in a page that may run for hours is a
 * leak. Oldest are dropped first.
 */
const K_MAX_ENTRIES = 500;

/**
 * Payload cap. Big enough for a full `session.update` (instructions plus ~46
 * tool schemas run tens of KB), small enough that 500 of them cannot pin an
 * unreasonable amount of memory.
 */
const K_MAX_PAYLOAD_CHARS = 60_000;

export interface IRealtimeConversationLog {
  /** Fires on every append and on clear, so the panel can re-render. */
  readonly onChanged: Emitter<void>;
  readonly entries: ReadonlyArray<RealtimeLogEntry>;
  logOutbound(event: Record<string, unknown>): void;
  logInbound(event: Record<string, unknown>): void;
  logTool(toolName: string, summary: string, payload?: unknown): void;
  note(type: string, summary?: string, payload?: unknown): void;
  logError(type: string, summary: string): void;
  clear(): void;
}

export class RealtimeConversationLog implements IRealtimeConversationLog {
  readonly onChanged = new Emitter<void>();

  private readonly m_entries: RealtimeLogEntry[] = [];
  private m_nextId = 1;
  private m_lastTimestamp = 0;

  constructor(private readonly m_recorder: RealtimeLogRecorder | null = null) {}

  get entries(): ReadonlyArray<RealtimeLogEntry> {
    return this.m_entries;
  }

  logOutbound(event: Record<string, unknown>): void {
    this.push("out", typeName(event), outboundSummary(event), event);
  }

  logInbound(event: Record<string, unknown>): void {
    if (shouldSuppressInboundEvent(event)) return;
    this.push("in", typeName(event), inboundSummary(event), event);
  }

  logTool(toolName: string, summary: string, payload?: unknown): void {
    this.push("tool", toolName, summary, payload);
  }

  note(type: string, summary?: string, payload?: unknown): void {
    this.push("note", type, summary, payload);
  }

  logError(type: string, summary: string): void {
    this.push("error", type, summary, undefined);
  }

  clear(): void {
    if (this.m_entries.length === 0) return;
    this.m_entries.length = 0;
    this.onChanged.emit();
  }

  private push(
    direction: RealtimeLogDirection,
    type: string,
    summary: string | undefined,
    payload: unknown,
  ): void {
    const timestamp = this.nextTimestamp();
    const entry: RealtimeLogEntry = {
      id: this.m_nextId++,
      at: timestamp,
      direction,
      type,
      summary: summary || undefined,
      payload: stringifyPayload(payload, { omitDeltaPayload: true }),
    };
    this.m_entries.push(entry);
    writeEntryToConsole(entry, payload);
    this.recordEntry(entry, payload);
    if (this.m_entries.length > K_MAX_ENTRIES) {
      this.m_entries.splice(0, this.m_entries.length - K_MAX_ENTRIES);
    }
    this.onChanged.emit();
  }

  private nextTimestamp(): number {
    const now = Date.now();
    const timestamp = Math.max(now, this.m_lastTimestamp + 1);
    this.m_lastTimestamp = timestamp;
    return timestamp;
  }

  private recordEntry(entry: RealtimeLogEntry, payload: unknown): void {
    if (!this.m_recorder) return;
    try {
      this.m_recorder({
        timestamp: entry.at,
        direction: entry.direction,
        type: entry.type,
        summary: entry.summary,
        payload: stringifyPayload(payload, { omitDeltaPayload: false }),
      });
    } catch (error) {
      console.warn("[RealtimeConversationLog] durable recorder failed:", error);
    }
  }
}

function writeEntryToConsole(entry: RealtimeLogEntry, payload: unknown): void {
  const label = `[RealtimeConversationLog] ${entry.direction} ${entry.type}`;
  const summary = entry.summary ? ` — ${entry.summary}` : "";
  if (isPlainRecord(payload)) {
    console.log(`${label}${summary}`, {
      ...payload,
      local_log_timestamp: entry.at,
    });
    return;
  }
  if (entry.payload) {
    console.log(`${label}${summary}\n${entry.payload}`);
    return;
  }
  console.log(`${label}${summary}`);
}

function typeName(event: Record<string, unknown>): string {
  return typeof event.type === "string" && event.type ? event.type : "(untyped)";
}

function isDeltaEvent(type: string): boolean {
  return type.endsWith(".delta");
}

function shouldSuppressInboundEvent(event: Record<string, unknown>): boolean {
  // Streaming deltas arrive by the hundred and are only meaningful once their
  // matching `.done`/`.completed` event arrives.
  return isDeltaEvent(typeName(event));
}

function inboundSummary(event: Record<string, unknown>): string | undefined {
  const type = typeName(event);
  if (isDeltaEvent(type)) {
    const delta = typeof event.delta === "string" ? event.delta : "";
    return `+${delta.length} chars`;
  }
  if (type === "error") {
    const error = event.error as Record<string, unknown> | undefined;
    return typeof error?.message === "string" ? error.message : undefined;
  }
  return undefined;
}

function outboundSummary(event: Record<string, unknown>): string | undefined {
  if (typeName(event) !== "conversation.item.create") return undefined;
  const item = event.item as Record<string, unknown> | undefined;
  const role = typeof item?.role === "string" ? item.role : "";
  return role ? `role=${role}` : undefined;
}

interface StringifyPayloadOptions {
  readonly omitDeltaPayload: boolean;
}

function stringifyPayload(payload: unknown, options: StringifyPayloadOptions): string | undefined {
  if (payload === undefined || payload === null) return undefined;
  if (typeof payload === "string") return truncate(payload);
  // A delta event's body is one fragment; the in-panel summary already reports its size.
  if (options.omitDeltaPayload && isPlainRecord(payload) && isDeltaEvent(typeName(payload))) {
    return undefined;
  }
  try {
    return truncate(JSON.stringify(payload, null, 2));
  } catch {
    // Circular or otherwise unserialisable: the type and summary still stand.
    return undefined;
  }
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function truncate(text: string): string {
  if (text.length <= K_MAX_PAYLOAD_CHARS) return text;
  return `${text.slice(0, K_MAX_PAYLOAD_CHARS)}\n… truncated (${text.length - K_MAX_PAYLOAD_CHARS} more characters)`;
}

export const REALTIME_CONVERSATION_LOG = Symbol.for("Campfire.RealtimeConversationLog");
